import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { ROLE_LABELS } from '@provalia/shared';
import { ThemeSelect } from '../../shared/ui';
import { PoweredBy } from '../../shared/brand';
import { usePortal } from '../context';
import { TenantLogo } from './Login';
import { SupplierLogo } from './status';
import { ViewAsBanner, ViewAsButton } from './ViewAs';

interface NavItem {
  to: string;
  label: string;
}

function navFor(role: string): NavItem[] {
  if (role === 'proveedor') {
    return [
      { to: '/', label: 'Mis pedidos' },
      { to: '/evaluacion', label: 'Mi evaluación' },
    ];
  }
  if (role === 'recepcion') {
    return [
      { to: '/', label: 'Recepción' },
      { to: '/proveedores', label: 'Proveedores' },
      { to: '/evaluacion', label: 'Evaluación' },
      { to: '/manual', label: 'Manual' },
    ];
  }
  const items: NavItem[] = [
    { to: '/', label: 'Cartera' },
    { to: '/proveedores', label: 'Proveedores' },
    { to: '/evaluacion', label: 'Evaluación' },
    { to: '/usuarios', label: 'Usuarios' },
    { to: '/avisos', label: 'Avisos' },
  ];
  if (role === 'cliente_admin') items.push({ to: '/configuracion', label: 'Configuración' });
  items.push({ to: '/manual', label: 'Manual' });
  return items;
}

export function Layout({ children }: { children: ReactNode }): JSX.Element {
  const { me, logout } = usePortal();
  const { session, tenant, supplier } = me;
  return (
    <div className="pt-shell">
      <ViewAsBanner />
      <header className="topbar">
        <div className="inner pt-topbar">
          <NavLink to="/" className="pt-brand" aria-label={`${tenant.name} · inicio`}>
            <TenantLogo info={tenant} className="logo" />
          </NavLink>
          <nav aria-label="Secciones">
            {navFor(session.role).map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === '/'}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="pt-user">
            {supplier ? (
              <span className="pt-supplier" title={`${supplier.name} (${supplier.cardCode})`}>
                <SupplierLogo cardCode={supplier.cardCode} hasLogo={supplier.hasLogo} name={supplier.name} size={30} />
                <span className="pt-supplier-name">{supplier.name}</span>
              </span>
            ) : null}
            <span className="pt-who">
              <span className="pt-who-name">{session.name}</span>
              <span className="subtle">{ROLE_LABELS[session.role]}</span>
            </span>
            <ViewAsButton />
            <ThemeSelect />
            <button className="btn sm" onClick={logout}>
              Salir
            </button>
          </div>
        </div>
        <div className="accent-bar" />
      </header>
      <main className="page pt-main">{children}</main>
      <footer className="pt-footer">
        <PoweredBy />
      </footer>
    </div>
  );
}
